import React from 'react';
import { Loader2, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';

function ChipList({ items }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-sm text-gray-500">Not available</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span
          key={item}
          className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function formatRate(hourlyRate) {
  const start = hourlyRate?.rangeStart;
  const end = hourlyRate?.rangeEnd;
  if (start == null || end == null) return 'Not suggested';
  return `$${start} - $${end}/hr`;
}

export default function JobFilterSuggestionModal({
  open,
  loading = false,
  saving = false,
  error = null,
  saveError = null,
  data = null,
  onSave,
  onSkip,
}) {
  if (!open) return null;

  const suggested = data?.suggested || null;
  const profileMeta = data?.profileMeta || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 p-4 flex items-center justify-center">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Review AI-Suggested Filters</h3>
            <p className="text-xs text-gray-500">Generated from your Upwork profile</p>
          </div>
        </div>

        {loading && (
          <div className="p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600 mx-auto" />
            <p className="mt-3 text-sm text-gray-600">Generating personalized filter suggestions...</p>
          </div>
        )}

        {!loading && error && (
          <div className="p-6">
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Could not load suggestions</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onSkip}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Continue Without Suggestions
              </button>
            </div>
          </div>
        )}

        {!loading && !error && !suggested && (
          <div className="p-6">
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">No suggestions available</p>
                <p className="text-sm text-yellow-700">
                  {data?.message || 'Your profile does not have enough information yet. You can configure filters manually later.'}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onSkip}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {!loading && !error && suggested && (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Profile Skills</p>
                <p className="text-sm font-semibold text-gray-900">{profileMeta.totalSkills ?? '-'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Completed Jobs</p>
                <p className="text-sm font-semibold text-gray-900">{profileMeta.totalJobs ?? '-'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-xs text-gray-500">Profile Rate</p>
                <p className="text-sm font-semibold text-gray-900">
                  {profileMeta.profileRate != null && profileMeta.rateCurrency
                    ? `${profileMeta.rateCurrency} ${profileMeta.profileRate}/hr`
                    : 'Not available'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Search Terms</p>
              <ChipList items={suggested.searchTerms} />
            </div>

            <div>
              <p className="text-sm font-medium text-gray-800 mb-2">Skills</p>
              <ChipList items={suggested.skills} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Experience Level</p>
                <p className="font-medium text-gray-900">{suggested.experienceLevel || 'ANY'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Job Type</p>
                <p className="font-medium text-gray-900">{suggested.jobType || 'ANY'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Hourly Rate</p>
                <p className="font-medium text-gray-900">{formatRate(suggested.hourlyRate)}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Posted Within</p>
                <p className="font-medium text-gray-900">{suggested.dateRange || 'ANY'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Verified Payment</p>
                <p className="font-medium text-gray-900">{suggested.verifiedPaymentOnly ? 'Required' : 'Not required'}</p>
              </div>
              <div className="rounded-lg border border-gray-200 px-3 py-2">
                <p className="text-gray-500 text-xs">Min Client Feedback</p>
                <p className="font-medium text-gray-900">{suggested.clientFeedbackMin ?? 'Any'}</p>
              </div>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-800">
                Save these suggestions to quickly initialize your job filters. You can edit them anytime in Job Filters settings.
              </p>
            </div>

            <div className="pt-2 flex items-center justify-end gap-2">
              {saveError && (
                <p className="mr-auto text-sm text-red-600">{saveError}</p>
              )}
              <button
                type="button"
                onClick={onSkip}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Skip for Now
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save and Continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
